import { mergeRight } from "./merge.js";
class Configurable {
  constructor(run) {
    this._run = run;
  }
  /**
   * Evaluate this configurable for a specific context.
   * @param Context The context in which the configurable should be evaluated.
   * @return The value it evaluates to for the respective context.
   */
  evaluateWithContext(context2) {
    if (this._resolved && this._resolved.context === context2) {
      return this._resolved.value;
    }
    const value = this._run(context2);
    this._resolved = {
      context: context2,
      value
    };
    return value;
  }
  /**
   * Derive a configurable from the evaluated value of another one.
   *
   * @param fn A function that takes the evaluated value of a configurable
   * and will return a new value or configurable.
   *
   * @returns A configurable which will evaluate to the same value as
   * the return value of `ref`.
   */
  chain(fn) {
    return new Configurable((context2) => {
      const result = this.evaluateWithContext(context2);
      const next = Configurable.of(fn(result));
      return next.evaluateWithContext(context2);
    });
  }
  /** Extract a property from a configurable object.
   *
   * @param propName A property name to extract from an object.
   * @returns A configurable which will evaluate to a property of the current configurable.
   *
   * @example
   * Configurable.of({x: 10}).prop('x')   // evaluates to 10
   *
   */
  prop(propName) {
    return this.chain((value) => value[propName]);
  }
  /** Defaults undefined values to the default object.
   *
   * @returns a new object with all the undefined properties replaced based on the parameter object.
   */
  defaults(value) {
    return this.chain(
      (val) => Configurable.of(value).chain((defaultVal) => {
        if (typeof val === "object") {
          return mergeRight(defaultVal, val || {});
        }
        return val || defaultVal;
      })
    );
  }
  /** Ensure a value is a configurable.
   *
   * @description
   * If it is a configurable, returns it immediately. Otherwise, return a configurable
   * that will evaluate to it.
   *
   */
  static of(x) {
    if (x instanceof Configurable) {
      return x;
    } else {
      return new Configurable(() => x);
    }
  }
  /** Create a configurable that will evaluate all properties of `obj`.
   *
   * @returns A configurable which will evaluate to an object, such that all
   * the property values of `obj` have been evaluated.
   *
   * @example
   * Configurable.props({
   *   x: Configurable.of(1),
   *   y: 2
   * })
   * // evaluates to {x: 1, y: 2}
   *
   */
  static props(obj) {
    return new Configurable((ctx) => {
      const entries = Object.entries(obj).map(([key, val]) => {
        const config = Configurable.of(val);
        return [key, config.evaluateWithContext(ctx)];
      });
      return Object.fromEntries(entries);
    });
  }
}
const context = new Configurable((ctx) => ctx);
function configurify(fn) {
  return function(...args) {
    return context.chain((context2) => {
      const resolvedArgs = args.map((arg) => Configurable.of(arg).evaluateWithContext(context2));
      return fn(...resolvedArgs);
    });
  };
}
export {
  Configurable,
  configurify,
  context
};
//# sourceMappingURL=configurable.js.map
